import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { canAccess } from "@/lib/roles";
import { fmtDateTime } from "@/lib/format";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { RefreshCw, CheckCircle2, AlertTriangle, XCircle, PlugZap } from "lucide-react";

const ago = (iso) => {
  const m = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  return h < 48 ? `${h}h ago` : `${Math.round(h / 24)}d ago`;
};

const STYLES = {
  ok: ["bg-emerald-50 text-emerald-700 border-emerald-200", CheckCircle2],
  stale: ["bg-amber-50 text-amber-700 border-amber-200", AlertTriangle],
  failed: ["bg-red-50 text-red-700 border-red-200", XCircle],
  off: ["bg-slate-50 text-slate-500 border-slate-200", PlugZap],
  running: ["bg-cyan-50 text-cyan-700 border-cyan-200", RefreshCw],
};

function stateOf(d) {
  if (!d?.connected) return "off";
  if (d.running) return "running";
  if (d.last_run?.status === "failed") return "failed";
  if (!d.last_success) return "stale";
  return Date.now() - new Date(d.last_success).getTime() > 2 * (d.interval_minutes || 60) * 60000 ? "stale" : "ok";
}

export const SyncStatusBadge = () => {
  const { user } = useAuth();
  const { data } = useQuery({
    queryKey: ["drchrono-sync-status"],
    queryFn: async () => (await api.get("/drchrono/sync-status")).data,
    refetchInterval: 120000,
    enabled: canAccess(user?.role, "drchrono") && !user?.must_change_password,
  });
  if (!data) return null;
  const s = stateOf(data);
  const [cls, Icon] = STYLES[s];
  const last = data.last_run;
  const label = s === "off" ? "EMR not connected" : s === "running" ? "Syncing…"
    : s === "failed" ? `Sync failed ${ago(last.ran_at)}` : data.last_success ? `Synced ${ago(data.last_success)}` : "Never synced";
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span data-testid="drchrono-sync-badge" data-state={s}
            className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full border ${cls}`}>
            <Icon className={`h-3.5 w-3.5 ${s === "running" ? "animate-spin" : ""}`} />
            <span className="hidden md:inline">DrChrono · </span><span className="hidden sm:inline">{label}</span>
          </span>
        </TooltipTrigger>
        <TooltipContent data-testid="drchrono-sync-tooltip" className="max-w-xs text-xs space-y-1">
          <div className="font-semibold">DrChrono EMR sync · every hour</div>
          <div className="sm:hidden">{label}</div>
          {data.last_success && <div>Last successful: {fmtDateTime(data.last_success)}</div>}
          {last && (
            <div>Last run: {fmtDateTime(last.ran_at)} ({last.trigger}) · {last.status}
              {last.status === "success" ? ` · ${last.patients ?? 0} patients, ${last.appointments ?? 0} appts` : ""}
            </div>
          )}
          {last?.status === "failed" && last.error && <div className="text-red-300">{last.error}</div>}
          {s === "stale" && <div>Data may be out of date — no successful sync in over 2 hours.</div>}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
